import { useEffect, useRef } from 'react';
import { loopAlertSound, getAlarmSettings } from '../utils/sounds';

// How late a reminder may still be worth hearing.
//
// This used to be 60 seconds, and it was the most serious defect in the app.
// The check runs every 30 s, which is fine while the app is awake in the
// foreground — but a phone in a pocket, screen off, has its timers slowed down
// or stopped altogether by the system. By the time the check ran again the
// 60-second window had passed, so the reminder never rang: no sound, no trace,
// nothing to notice. "MeMo sometimes doesn't warn me" is fatal for a reminder
// app — unreliable sometimes is unreliable always.
//
// Fifteen minutes covers the ordinary case (the phone dozing for a few minutes)
// without ever producing something absurd, like an alarm for this morning's
// dentist going off at supper. A reminder two minutes late is still a reminder.
const LATE_MS = 15 * 60 * 1000;

// Which reminders have already rung — kept in localStorage, not only in memory.
// Widening the window above makes a reload inside it much more likely, and a
// reminder that rings twice is its own kind of broken.
const FIRED_KEY = 'memo_fired_alerts_v1';
const KEEP_MS   = 24 * 60 * 60 * 1000;

function loadFired() {
  try {
    const raw = JSON.parse(localStorage.getItem(FIRED_KEY) || '{}');
    const now = Date.now();
    const kept = {};
    // Drop yesterday's entries so the list cannot grow forever.
    Object.keys(raw).forEach((k) => {
      if (typeof raw[k] === 'number' && now - raw[k] < KEEP_MS) kept[k] = raw[k];
    });
    return kept;
  } catch (_) {
    return {};
  }
}

function saveFired(map) {
  try { localStorage.setItem(FIRED_KEY, JSON.stringify(map)); } catch (_) {}
}

/**
 * Checks loaded events every 30 seconds — and again the moment the app comes
 * back to the foreground — firing an in-app alert (looping sound + toast
 * callback) when an event's reminder time is reached.
 *
 * @param {Array}    events  - Array of event objects: { id, start, title, alertMinutes, alertSound }
 * @param {Function} onAlert - Called as onAlert(event, stopFn) when an alert fires.
 *                             stopFn() stops the looping sound; caller is responsible for calling it.
 */
export function useAlertScheduler(events, onAlert) {
  // Which alerts have already fired — survives a reload, unlike the old Set.
  const firedRef = useRef(loadFired());
  // Track active stop functions so we can clean them up on unmount
  const activeStopsRef = useRef(new Map()); // key → stopFn

  useEffect(() => {
    if (!events?.length) return undefined;

    function check() {
      const now = Date.now();
      events.forEach(event => {
        if (!event.start) return;

        // An event may have several reminders (e.g. 60 + 10 min) — ring for EACH.
        const mins = Array.isArray(event.alertMinutesList) && event.alertMinutesList.length
          ? event.alertMinutesList
          : (event.alertMinutes != null ? [event.alertMinutes] : []);
        if (!mins.length) return;

        const eventMs = new Date(event.start).getTime();
        if (!Number.isFinite(eventMs)) return;

        mins.forEach(minutesBefore => {
          const alertMs = eventMs - minutesBefore * 60 * 1000;
          const key     = `${event.id}_${alertMs}`;

          // Due, not yet rung, and not so late that it would be noise.
          if (firedRef.current[key]) return;
          if (now < alertMs || now >= alertMs + LATE_MS) return;

          firedRef.current[key] = now;
          saveFired(firedRef.current);

          // Only the "alarm" reminder type rings; "message" is a silent
          // on-screen notification (no sound).
          let stop = () => {};
          if (event.alertType === 'alarm') {
            const { volume, sound } = getAlarmSettings();
            stop = loopAlertSound(sound, volume).stop;
          }

          activeStopsRef.current.set(key, stop);
          onAlert?.(event, () => {
            stop();
            activeStopsRef.current.delete(key);
          });
        });
      });
    }

    check();                               // check immediately on mount / events change
    const id = setInterval(check, 30_000); // then every 30 s

    // The moment the phone is picked up again. This is the half of the fix that
    // matters most: the system may have frozen the interval for minutes, and
    // waiting up to another 30 s to notice would waste part of the window we
    // just widened. Coming back to the app checks straight away.
    const onWake = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [events, onAlert]);

  // Stop any still-ringing alarm ONLY when the scheduler truly unmounts — never
  // on a routine `events` change. Previously this cleanup ran on every refresh
  // of the events list (every few minutes, and whenever the app regained focus),
  // which silenced an alarm that was actively going off.
  useEffect(() => {
    const active = activeStopsRef.current;
    return () => {
      active.forEach(stopFn => stopFn());
      active.clear();
    };
  }, []);
}
